/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	CosmosTagsAndAttribution,
	CosmosEmbed,
	CosmosImage,
	CosmosVideo,
	OptimizedCosmosAssetFragment,
	CosmosTagProps,
} from "@components/elements";
import { useHygraphImageLoader, useDisabledHygraphImageLoader } from "@components/hooks/imageloaders.hooks";
import { getOptAsset } from "@components/utils/assetUtils";
import type { BlogTagFragment } from "@cosmos/hygraph-schema";

export interface CosmosAssetProps extends React.ImgHTMLAttributes<HTMLImageElement> {
	asset?: OptimizedCosmosAssetFragment | null;
	tags?: CosmosTagProps[] | BlogTagFragment[] | null;
	className?: string;
	attributionClassName?: string;
	disableAutoplay?: boolean;
	hideAttribution?: boolean;
	eager?: boolean;
	sizes?: string;
	mediaRef?: (el: HTMLImageElement | HTMLVideoElement | null) => void;
}

export const CosmosAsset = ({
	asset,
	tags,
	className,
	disableAutoplay,
	attributionClassName,
	hideAttribution,
	eager,
	sizes,
	mediaRef,
	crossOrigin,
}: CosmosAssetProps): React.JSX.Element | null => {
	const disabledHygraphImageLoader = useDisabledHygraphImageLoader();
	const hygraphImageLoader = useHygraphImageLoader();

	if (asset) {
		if (asset.overrideEmbedUrl) {
			return (
				<CosmosEmbed
					className={className}
					iframeSrc={asset.overrideEmbedUrl}
					overlayImage={{
						...asset,
						optUrl: "",
					}}
					disableOverlay={!disableAutoplay && asset.overrideEmbedDisableOverlay}
					attribution={asset.attribution}
					playButtonAriaLabel={asset.altText}
					attributionClassName={attributionClassName}
				/>
			);
		}

		const type = asset.mimeType;

		// TODO: consider other delineations like:
		// case type === "application/msword" for mammoth.js converter
		// case type === "application/pdf" or "application/zip" for simple downloads
		// case type === "text/html" or "text/css" for manual DOM updates without code deploys

		if (type?.startsWith("video/")) {
			return (
				<CosmosVideo
					className={className}
					video={asset}
					disableAutoplay={disableAutoplay}
					attributionClassName={attributionClassName}
					mediaRef={mediaRef}
					crossOrigin={crossOrigin}
				/>
			);
		}

		// when mimeType is image/* and by default try to render an image
		return (
			<CosmosTagsAndAttribution
				tags={tags}
				attribution={!hideAttribution ? asset.attribution : null}
				className={attributionClassName}
			>
				<CosmosImage
					loading={eager ? "eager" : "lazy"}
					className={className}
					width={asset?.width || 0}
					height={asset?.height || 0}
					src={getOptAsset(asset)?.url || ""}
					unoptimized={!!asset?.optUrl}
					alt={asset?.altText || ""}
					loader={asset?.optUrl || type?.startsWith("image/svg") ? disabledHygraphImageLoader : hygraphImageLoader}
					fetchPriority={eager ? "high" : "auto"}
					sizes={sizes || "100vw"}
					priority={eager}
					crossOrigin={crossOrigin}
					mediaRef={mediaRef}
				/>
			</CosmosTagsAndAttribution>
		);
	}
	return null;
};
